import { addDays, addWeeks, addMonths, isAfter } from "date-fns";
import type { EventRow } from "../types/event";
import { MAX_RECURRENCE } from "../config";

export function parseRRule(rr: string): { freq: 'DAILY'|'WEEKLY'|'MONTHLY'; interval: number; count?: number; until?: string } {
  const parts = Object.fromEntries(rr.split(';').map(p => p.split('=')));
  const freq = (parts['FREQ'] || parts['freq'] || 'DAILY') as any;
  const interval = parseInt(parts['INTERVAL'] || '1', 10) || 1;
  const count = parts['COUNT'] ? parseInt(parts['COUNT'], 10) : undefined;
  const until = parts['UNTIL'];
  const supported: any = { DAILY: 'DAILY', WEEKLY: 'WEEKLY', MONTHLY: 'MONTHLY' };
  return { freq: supported[freq] || 'DAILY', interval, count, until };
}

export function expandRecurrence(rows: EventRow[]): EventRow[] {
  const result: EventRow[] = [];
  const now = new Date();
  for (const r of rows) {
    if (!r.rrule) { result.push(r); continue; }
    const rule = parseRRule(r.rrule);
    const baseStart = new Date(r.start_iso);
    const baseEnd = r.end_iso ? new Date(r.end_iso) : null;
    if (isNaN(+baseStart)) { result.push(r); continue; }

    const until = rule.until ? new Date(rule.until) : null;
    const ex = new Set((r.exdates || "").split(',').map(s => s.trim()).filter(Boolean));

    let iter = 0;
    let curStart = baseStart;
    let curEnd = baseEnd;
    while (iter < (rule.count || MAX_RECURRENCE)) {
      const curIso = curStart.toISOString();
      if (!ex.has(curIso.slice(0,19))) {
        const ev: EventRow = { ...r, start_iso: curStart.toISOString(), end_iso: curEnd ? curEnd.toISOString() : r.end_iso };
        result.push(ev);
      }
      if (rule.freq === 'DAILY') {
        curStart = addDays(curStart, rule.interval);
        curEnd = curEnd ? addDays(curEnd, rule.interval) : curEnd;
      } else if (rule.freq === 'WEEKLY') {
        curStart = addWeeks(curStart, rule.interval);
        curEnd = curEnd ? addWeeks(curEnd, rule.interval) : curEnd;
      } else if (rule.freq === 'MONTHLY') {
        curStart = addMonths(curStart, rule.interval);
        curEnd = curEnd ? addMonths(curEnd, rule.interval) : curEnd;
      } else {
        break;
      }
      iter++;
      if (until && isAfter(curStart, until)) break;
    }
  }
  result.sort((a,b) => +new Date(a.start_iso) - +new Date(b.start_iso));
  return result;
}